/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/** Cookie the backend reads to identify the client app version. */
export const APP_VERSION_COOKIE_NAME: string = "x-app-version";

/**
 * How long the cookie lives past the app launch: 9 hours. Without this the
 * cookie would be a session cookie, which is what it was previously.
 */
export const APP_VERSION_COOKIE_MAX_AGE_SECONDS: number = 9 * 60 * 60;

/**
 * Mirrors the app version onto a cookie for this origin so it travels with
 * every request the Web App makes. The value comes from the shell app via
 * the `x-app-version` query parameter — see `getAppVersionFromUrl()`.
 *
 * Both `Max-Age` and `Expires` are set to 9 hours from the launch, i.e. from
 * this call. `Max-Age` wins wherever it is supported; `Expires` is the
 * fallback for older TV browser builds that ignore it.
 */
export function setAppVersionCookie(version: string): void {
  const expiryDate: Date = new Date(
    Date.now() + APP_VERSION_COOKIE_MAX_AGE_SECONDS * 1000
  );
  document.cookie =
    `${APP_VERSION_COOKIE_NAME}=${version}` +
    `; path=/` +
    `; Expires=${expiryDate.toUTCString()}` +
    `; Max-Age=${APP_VERSION_COOKIE_MAX_AGE_SECONDS}` +
    `; Secure; SameSite=None`;
}

/**
 * Expires the app version cookie so the next request from this origin carries
 * no `x-app-version` at all and the serving layer falls back to the default
 * build. The counterpart to `setAppVersionCookie()`, and the only supported way
 * to remove it — `path`, `Secure` and `SameSite` must match the values the
 * cookie was written with or the browser treats this as a *different* cookie
 * and leaves the original in place.
 *
 * Used by the debug-only version override's Reset action
 * (`src/devtools/appVersionOverride.ts`). Nothing in the normal shell-driven
 * flow clears the cookie: it either carries the shell's version or expires on
 * its own after `APP_VERSION_COOKIE_MAX_AGE_SECONDS`.
 */
export function clearAppVersionCookie(): void {
  document.cookie =
    `${APP_VERSION_COOKIE_NAME}=` +
    `; path=/` +
    `; Expires=Thu, 01 Jan 1970 00:00:00 GMT` +
    `; Max-Age=0` +
    `; Secure; SameSite=None`;
}
